/**
 * Rotas (Iter 98 — Fase 1): cadastro de rotas por cor + vínculo N:N com bairros.
 * Ao lançar uma entrega, a rota é definida automaticamente pelo bairro quando ele
 * pertence a UMA rota ativa; em várias, o usuário escolhe; em nenhuma → "Sem rota".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Route, ListChecks, TrendingUp, Map as MapIcon } from "lucide-react";
import { useSearchParams, useParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RotasTable } from "./rotas/RotasTable";
import { RotaDialog } from "./rotas/RotaDialog";
import { RotasDesempenho } from "./rotas/RotasDesempenho";
import { RotasMapa } from "./rotas/RotasMapa";

export default function Rotas() {
  const { user, currentEmpresaId } = useAuth();
  const [params, setParams] = useSearchParams();
  const { slug } = useParams();
  const tab = ["cadastro", "desempenho", "mapa"].includes(params.get("tab")) ? params.get("tab") : "cadastro";
  const setTab = (t) => setParams(t === "cadastro" ? {} : { tab: t }, { replace: true });
  const [rotas, setRotas] = useState([]);
  const [bairros, setBairros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, rota: null });
  const [toDelete, setToDelete] = useState(null);
  const canI = hasPerm(user, "rotas", "i");
  const canA = hasPerm(user, "rotas", "a");
  const canE = hasPerm(user, "rotas", "e");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([api.get("/rotas"), api.get("/bairros")]);
      setRotas(r.data || []); setBairros(b.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar rotas");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentEmpresaId]);

  const bairroName = useMemo(() => Object.fromEntries(bairros.map((b) => [b.id, b.nome])), [bairros]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rotas.filter((r) => (r.nome || "").toLowerCase().includes(q)) : rotas;
  }, [rotas, search]);
  const ativas = rotas.filter((r) => r.ativa !== false).length;
  const bairrosSemRota = useMemo(() => {
    const covered = new Set(rotas.flatMap((r) => r.bairro_ids || []));
    return bairros.filter((b) => !covered.has(b.id)).length;
  }, [rotas, bairros]);

  const toggleAtiva = async (r) => {
    try {
      await api.put(`/rotas/${r.id}`, { nome: r.nome, cor: r.cor, ativa: r.ativa === false, bairro_ids: r.bairro_ids || [] });
      toast.success(r.ativa === false ? `Rota "${r.nome}" ativada` : `Rota "${r.nome}" desativada`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar rota"); }
  };
  const remove = async () => {
    const r = toDelete; if (!r) return;
    try {
      const res = await api.delete(`/rotas/${r.id}`);
      const n = res.data?.entregas_desvinculadas || 0;
      toast.success(`Rota "${r.nome}" excluída${n ? ` · ${n} entrega(s) ficaram sem rota` : ""}`);
      setToDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir rota"); }
  };

  return (
    <div className="space-y-6" data-testid="page-rotas">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h1 className="text-4xl font-extrabold flex items-center gap-3"><Route className="w-9 h-9 text-orange-600" />Rotas</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="rotas-tabs">
              <TabsTrigger value="cadastro" data-testid="rotas-tab-cadastro"><ListChecks className="w-4 h-4 mr-1" />Cadastro</TabsTrigger>
              <TabsTrigger value="desempenho" data-testid="rotas-tab-desempenho"><TrendingUp className="w-4 h-4 mr-1" />Desempenho</TabsTrigger>
              <TabsTrigger value="mapa" data-testid="rotas-tab-mapa"><MapIcon className="w-4 h-4 mr-1" />Mapa</TabsTrigger>
            </TabsList>
          </Tabs>
          {canI && tab === "cadastro" && (
            <Button onClick={() => setDialog({ open: true, rota: null })} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-nova-rota">
              <Plus className="w-4 h-4 mr-2" />Nova Rota
            </Button>
          )}
        </div>
      </div>

      {tab === "desempenho" && <RotasDesempenho />}
      {tab === "mapa" && <RotasMapa />}

      {tab === "cadastro" && (<>
      <div className="text-xs text-muted-foreground bg-orange-50 border border-orange-200 rounded p-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span><b>Como funciona:</b> uma rota agrupa bairros. Ao lançar uma entrega, a rota é atribuída automaticamente pelo bairro (quando ele pertence a uma única rota ativa).</span>
        <span className="ml-auto flex gap-2">
          <span className="rounded-full bg-white border px-2 py-0.5" data-testid="rotas-stat-total">{rotas.length} rota{rotas.length === 1 ? "" : "s"}</span>
          <span className="rounded-full bg-white border px-2 py-0.5 text-emerald-700" data-testid="rotas-stat-ativas">{ativas} ativa{ativas === 1 ? "" : "s"}</span>
          <span className={`rounded-full bg-white border px-2 py-0.5 ${bairrosSemRota ? "text-amber-700" : ""}`} data-testid="rotas-stat-bairros-sem-rota">{bairrosSemRota} bairro{bairrosSemRota === 1 ? "" : "s"} sem rota</span>
        </span>
      </div>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center gap-3 flex-wrap">
          <CardTitle data-testid="rotas-list-title">Cadastros ({filtered.length}{search ? ` de ${rotas.length}` : ""})</CardTitle>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar rota" className="pl-8 w-48 h-9" data-testid="rotas-search" />
          </div>
        </CardHeader>
        <CardContent>
          <RotasTable rotas={filtered} bairroName={bairroName} canEdit={canA} canDelete={canE} loading={loading}
            entregasHref={(r) => `/${slug}/entregas?rota_id=${r.id}`}
            onEdit={(r) => setDialog({ open: true, rota: r })} onDelete={setToDelete} onToggleAtiva={toggleAtiva} />
        </CardContent>
      </Card>
      </>)}

      <RotaDialog open={dialog.open} rota={dialog.rota} bairros={bairros} rotas={rotas}
        onClose={() => setDialog({ open: false, rota: null })} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="rota-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir rota "{toDelete?.nome}"?</AlertDialogTitle>
            <AlertDialogDescription>
              As entregas vinculadas a esta rota ficarão como "Sem rota". Os bairros não são apagados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rota-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="rota-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
